// // hooks/usePassportOCR.ts
import { useState } from "react";
import { scanPassportOCR } from "services/ocrService";
import { toast } from "sonner";
import axios from "axios";

export interface PassportOCRResponse {
    passportNumber?: string;
    placeOfIssue?: string;
    arabicName?: string;
    passportType?:string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    gender?: string;
    issueDate?: string | null;
    expiryDate?: string | null;
    dateOfBirth?: string | null;
    nationality?: string;
}

export const usePassportOCR = () => {
    const [ocrLoading, setOcrLoading] = useState(false);
    const [ocrError, setOcrError] = useState<string | null>(null);

    const scanPassport = async (
        file: File,
        documentType?: string,
        documentCode?: string,
    ): Promise<PassportOCRResponse | null> => {

        // Only run OCR for Passport Copy
        const normalizedDocType = documentType?.trim().toLowerCase();
        const normalizedDocCode = documentCode?.trim().toUpperCase();
        if (normalizedDocCode !== "PASSPORT" && normalizedDocType !== "passport copy") {
            // Non-passport documents do not require passport OCR.
            setOcrError(null);
            return null;
        }

        // OCR API only supports images
        const isImage = file.type.startsWith("image/");
        if (!isImage) {
            const message = "OCR extraction is supported for image files only.";
            setOcrError(message);
            toast.error(message);
            return null;
        }

        try {
            setOcrLoading(true);
            setOcrError(null);
            const data = await scanPassportOCR(file);
            return data as PassportOCRResponse;
        } catch (err: unknown) {
            const apiMessage = axios.isAxiosError(err)
                ? err.response?.data?.message || err.response?.data?.detail
                : undefined;
            const errorMessage = axios.isAxiosError(err) && err.response?.status === 422
                ? apiMessage || "This image could not be recognized as a valid passport. Please upload a clear passport bio-data page."
                : apiMessage || (err instanceof Error ? err.message : "Failed to extract passport details.");
            setOcrError(errorMessage);
            toast.error(errorMessage);
            return null;
        } finally {
            setOcrLoading(false);
        }
    };

    return {
        scanPassport,
        ocrLoading,
        ocrError,
    };
};
